import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import "../App.css";
import axios from "axios";
import api from "../Interceptor";
import { getRoute } from "../api/routes";
import { useSelector, useDispatch } from "react-redux";
import { getCurrencyName } from "../utils/getCurrencyName";
import { CustomInput, CustomSelect, ErrorText } from "../components/ui";
import CustomModal from "../components/customModal/customModal";
import ModalFooter from "../components/customModal/modalFooter";
import { DatePicker } from "antd";
import { Link } from "react-router-dom";
import {
  validateFormHandler,
  validateLiveField,
} from "../utils/validations/validateForm";
import { setRecentTransactions } from "../slices/app";
import { fetchTransactions } from "../api/services/allExpenses";

const AddExpense = ({ onSuccess, onCancel, isModalOpen, projectId }) => {
  const user = useSelector((state) => state.auth.user);
  const symbol = useSelector((state) => state.currency.currencies);
  const baseCurrency = localStorage.getItem("baseCurrency");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [project, setProject] = useState("");
  const [milestone, setMilestone] = useState("");
  const { projectList } = useSelector((state) => state.app);
  const [errors, setErrors] = useState({});
  const [milestonesList, setMilestonesList] = useState([]);
  const [projectCur, setProjectCur] = useState("");
  const [type, setType] = useState("income");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMilestoneData, setSelectedMilestoneData] = useState(null);
  const [showFooterShadow, setShowFooterShadow] = useState(false);
  const contentRef = useRef(null);
  const dispatch = useDispatch();

  const fetchCurrencyRates = async (currencyId) => {
    try {
      const currencySymbol = await getCurrencyName(currencyId);
      if (currencySymbol === "N/A") {
        throw new Error("Invalid currency ID or symbol not found");
      }

      const url = `https://api.exchangerate-api.com/v4/latest/${currencySymbol}`;
      const response = await axios.get(url);

      return response.data.rates;
    } catch (error) {
      console.error("Error fetching currency rates:", error.message);
      return null;
    }
  };
  const fetchMilestones = async (projectId) => {
    try {
      const response = await api.get(
        `${getRoute(
          "users",
          `${user.userRole.name}`,
          `${user.userRole.name === "admin" ? "projects" : "milestone"}`,
          false,
          false,
          `${user.userRole.name === "admin" ? `milestone` : ""}`
        )}?projectId=${projectId}`
      );
      setMilestonesList(response.data);
    } catch (error) {
      toast.error("Error fetching milestones", error);
      return null;
    }
  };

  const handleProjectChange = (selectedProjectId) => {
    const selectedProjectData = projectList.find(
      (proj) => proj?._id === selectedProjectId
    );
    if (selectedProjectData) {
      setProject(selectedProjectData._id);
      setProjectCur(selectedProjectData.currency.name);
      fetchMilestones(selectedProjectId);
    } else {
      setProject("");
      setProjectCur("");
    }
  };
  const handleMilestoneChange = (selectedMilestoneId) => {
    const selectedMilestoneData = milestonesList.find(
      (milestone) => milestone._id === selectedMilestoneId
    );
    if (selectedMilestoneData) {
      setMilestone(selectedMilestoneData._id);
      setSelectedMilestoneData(selectedMilestoneData);
    } else {
      setMilestone("");
      setSelectedMilestoneData(null);
    }
  };
  // Auto-select project when projectId prop is provided
  useEffect(() => {
    if (isModalOpen && projectId) {
      // Find the project in projectList
      const selectedProject = projectList.find(
        (proj) => proj._id === projectId
      );
      if (selectedProject) {
        setProject(projectId);
        setProjectCur(selectedProject.currency?.name || "");
        fetchMilestones(projectId);
      }
    }
  }, [isModalOpen, projectId, projectList]);
  const disabledDate = (current) => {
    if (!selectedMilestoneData) {
      // If no milestone is selected, only allow dates up to current date
      return current && current > dayjs().endOf("day");
    }

    const milestoneStartDate = dayjs(selectedMilestoneData.startDate);
    const milestoneEndDate = dayjs(selectedMilestoneData.endDate);
    const currentDate = dayjs().endOf("day");

    // Determine the actual end date (whichever is earlier: milestone end date or current date)
    const actualEndDate = milestoneEndDate.isBefore(currentDate)
      ? milestoneEndDate
      : currentDate;

    return (
      current &&
      (current < milestoneStartDate.startOf("day") || current > actualEndDate)
    );
  };
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);

    const error = validateLiveField("name", value);

    setErrors((prev) => ({ ...prev, name: error || undefined }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const rates = await fetchCurrencyRates(baseCurrency);
      if (!rates) {
        toast.error("Failed to fetch currency rates.");
        setIsLoading(false);
        return;
      }

      const conversionRate = rates[projectCur] || 1;

      const expenseData = {
        name,
        amount: Number(amount),
        type,
        conversionRate,
        date,
        milestone,
        description: description || undefined,
      };

      const validationErrors = validateFormHandler({
        name,
        amount: Number(amount),
        type,
        conversionRate,
        date,
        milestone,
        project,
      });
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      const response = await api.post(
        getRoute("users", `${user.userRole.name}`, "expenses", false),
        expenseData
      );

      if (response.data && response.data.message) {
        onCancel();
        toast.success(response.data.message);
      } else {
        toast.success("Expense saved successfully!");
        handleCancel();
        if (onSuccess) {
          onSuccess();
          fetchTransactions().then((state) =>
            dispatch(setRecentTransactions(state))
          );
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        error.response?.data?.error ||
          "An error occurred while saving the expense."
      );
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancel = () => {
    setName("");
    setAmount("");
    setDescription("");
    setDate("");
    setType("income");
    setProjectCur("");
    setMilestonesList([]);
    onCancel();
  };
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkScrollable = () => {
      setShowFooterShadow(el.scrollHeight > el.clientHeight);
    };

    checkScrollable();

    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, []);
  return (
    <CustomModal
      modalTitle="Add Transaction"
      isOpen={isModalOpen}
      onClose={handleCancel}
    >
      <form onSubmit={handleSubmit}>
        <div
          ref={contentRef}
          className="w-full mx-auto bg-white max-h-[50vh] scrollbar-hidden font-customFont"
        >
          {project &&
            selectedMilestoneData &&
            dayjs(selectedMilestoneData.startDate).isAfter(dayjs(), "day") && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-2 rounded-2xl shadow-sm">
                <div className="flex items-center justify-center min-w-6 h-6 rounded-full bg-red-100 text-red-600 font-bold">
                  i
                </div>
                <p className="text-sm md:text-base">
                  Future transactions are not allowed. Update the start date of{" "}
                  <Link
                    to="/projects"
                    state={{ milestoneId: selectedMilestoneData?._id }}
                    className="underline cursor-pointer font-semibold"
                  >
                    {`'${selectedMilestoneData.name}'`}
                  </Link>{" "}
                  in Projects tab to update start date to today or an earlier
                  date.
                </p>
              </div>
            )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <div className="mb-4">
                <CustomSelect
                  label="Project"
                  name="project"
                  value={project}
                  onChange={(e) => {
                    handleProjectChange(e);
                    if (errors.project) {
                      setErrors((prev) => ({ ...prev, project: undefined }));
                    }
                  }}
                  options={
                    projectList?.length
                      ? projectList.map((proj) => ({
                          label: proj?.name,
                          value: proj?._id,
                        }))
                      : []
                  }
                  placeholder="Select Project"
                  message={errors.project}
                  requir="true"
                />
              </div>
              <div className="mb-4">
                <CustomInput
                  type="text"
                  value={name}
                  onChange={handleNameChange}
                  label="Title"
                  message={errors.name}
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  {projectCur && (
                    <div className="absolute left-0 m-[1px] top-0 bottom-0 flex items-center pl-3 pr-2 bg-gray-100 border-r border-gray-300 rounded-l-md text-gray-700 text-sm">
                      {projectCur}
                    </div>
                  )}
                  <CustomInput
                    type="number"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      if (errors.amount) {
                        setErrors((prev) => ({ ...prev, amount: undefined }));
                      }
                    }}
                    className={projectCur ? "pl-16" : ""}
                  />
                </div>
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                )}
              </div>
            </div>
            <div>
              <div className="mb-4">
                <CustomSelect
                  label="Milestone"
                  value={milestone}
                  onChange={(e) => {
                    handleMilestoneChange(e);
                    if (errors.milestone) {
                      setErrors((prev) => ({ ...prev, milestone: undefined }));
                    }
                  }}
                  options={
                    milestonesList?.length > 0
                      ? milestonesList.map((milestone) => ({
                          label: milestone.name,
                          value: milestone._id,
                        }))
                      : []
                  }
                  message={errors.milestone}
                  requir="true"
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="date"
                  className="block font-medium mb-1 text-gray-700"
                >
                  Date <span className="text-red-500">*</span>
                </label>

                <DatePicker
                  id="date"
                  name="date"
                  className="w-full h-[43px] rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                  value={date ? dayjs(date) : null}
                  disabledDate={disabledDate}
                  onChange={(value) => {
                    const selectedDate = value
                      ? value.format("YYYY-MM-DD")
                      : "";
                    setDate(selectedDate);
                    if (errors.date) {
                      setErrors((prev) => ({ ...prev, date: undefined }));
                    }
                  }}
                />

                {errors.date && <ErrorText message={errors.date} />}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-1">
                  Transaction Type
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor flex justify-between">
                  <CustomInput
                    type="radio"
                    value="income"
                    label="Income"
                    checked={type === "income"}
                    onChange={() => setType("income")}
                    className="custom-radio px-1 py-1"
                  />
                  <CustomInput
                    type="radio"
                    value="expense"
                    label="Expense"
                    checked={type === "expense"}
                    onChange={() => setType("expense")}
                    className="custom-radio"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="mb-4">
              <CustomInput
                label="Description"
                as="textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>
          </div>
        </div>
        <ModalFooter
          onCancel={handleCancel}
          isLoading={isLoading}
          showShadow={showFooterShadow}
        />
      </form>
    </CustomModal>
  );
};

export default AddExpense;
